<template>
    <div class="relative user-select-none w-40 h-40 md:w-60 md:h-60 rounded-[1rem] overflow-hidden shadow-lg group shrink-0 transition-transform duration-300 border border-gray-100 dark:border-slate-700">
        <!-- Placeholder background (shown until the cover loads or when it fails) -->
        <div class="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-200 to-indigo-100 dark:from-slate-700 dark:via-slate-800 dark:to-indigo-900"></div>

        <!-- Full Background Image: hidden until loaded so a slow load never shows a broken image -->
        <img
            v-if="cover && !coverFailed"
            :src="cover"
            loading="lazy"
            decoding="async"
            class="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
            :class="coverLoaded ? 'opacity-100' : 'opacity-0'"
            alt="Playlist Cover"
            @load="coverLoaded = true"
            @error="coverFailed = true"
        />

        <!-- Glassmorphism Overlay -->
        <div class="absolute inset-x-0 bottom-0  bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-16 flex flex-col justify-end" >
            <div class="backdrop-blur-md bg-white/10 border-t border-white/10 p-3 rounded-b-[1rem] shadow-sm">
                <h3 class="font-bold text-white text-sm md:text-base leading-tight truncate mb-0.5">{{ playlist.name }}</h3>
                <p class="text-[10px] md:text-xs text-gray-200 truncate opacity-90 user-select-none">
                    {{ playlist.description || 'No description' }}
                </p>
            </div>
        </div>

        <!-- Offline Action Button / Status (Visible always if downloaded/downloading/update available, or on hover) -->
        <div class="absolute top-3" :class="props.editVisible ? 'right-12' : 'right-3'">
            <Tooltip position="bottom" :text="isOfflineDownloading ? ($t('playlistView.downloading', { current: offlineProgress.current, total: offlineProgress.total || playlist.songs?.length || 0 }) || 'Baixando...') : (hasUpdate ? ($t('playlistView.updateAvailableTooltip') || 'Atualização disponível — Clique para sincronizar') : (isOffline ? ($t('playlistView.offlineReady') || 'Disponível offline') : ($t('playlistView.saveOffline') || 'Salvar offline')))">
                <button
                    class="p-2 backdrop-blur-md rounded-full transition-all duration-300 hover:scale-110 cursor-pointer flex items-center justify-center border-none shadow-sm"
                    :class="[
                        isOfflineDownloading
                            ? 'bg-indigo-600/90 text-white opacity-100 cursor-wait'
                            : hasUpdate
                                ? 'bg-amber-500/90 hover:bg-amber-600/90 text-white opacity-100 ring-2 ring-amber-300 animate-pulse'
                                : isOffline
                                    ? 'bg-emerald-600/90 text-white opacity-100 hover:bg-emerald-700/90'
                                    : 'bg-black/20 hover:bg-black/40 text-white opacity-0 group-hover:opacity-100'
                    ]"
                    :disabled="isOfflineDownloading"
                    @click.stop="toggleOffline"
                    @touchstart.prevent.stop="toggleOffline"
                    :aria-label="hasUpdate ? 'Update offline playlist' : (isOffline ? 'Remove offline playlist' : 'Download playlist offline')"
                >
                    <ArrowPathIcon v-if="isOfflineDownloading" class="w-4 h-4 animate-spin text-white" />
                    <ArrowPathRoundedSquareIcon v-else-if="hasUpdate" class="w-4 h-4 text-white" />
                    <CheckCircleIcon v-else-if="isOffline" class="w-4 h-4 text-white" />
                    <CloudArrowDownIcon v-else class="w-4 h-4 text-white" />
                </button>
            </Tooltip>
        </div>

        <!-- Edit Button (Hidden by default, visible on hover) -->
        <div class="absolute top-3 right-3">
            <Tooltip position="bottom" :text="$t('playlist.edit')" class="ml-2">
                <button v-if="props.editVisible"
                    class="p-2 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full text-white opacity-60 group-hover:opacity-100 transition-all duration-300 hover:scale-110 cursor-pointer"
                    @click.stop="onEdit"
                    @touchstart.prevent="onEdit"
                    aria-label="Edit playlist"
                >
                    <PencilIcon class="w-4 h-4" />
                </button>
            </Tooltip>
        </div>
        <!-- Favorite Button (Hidden by default, visible on hover) -->
        <div class="absolute top-3 left-3">
            <Tooltip position="bottom" :text="$t('playlist.favorite')" class="ml-2">
                <button
                    class="p-2 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full transition-all duration-300 hover:scale-110 cursor-pointer"
                    :class="{ 'opacity-60 group-hover:opacity-100 text-white': !playlist.isFavorite, 'text-yellow-300 opacity-100': playlist.isFavorite }"
                    @click.stop="onFavorite"
                    @touchstart.prevent="onFavorite"
                    aria-label="Toggle favorite playlist"
                >
                    <StarIcon class="w-4 h-4" />
                </button>
            </Tooltip>
        </div>
        <!-- Play Button (Hidden by default, visible on hover) -->
        <div class="absolute bottom-3 right-3">
            <Tooltip position="bottom" :text="$t('playlist.play')" class="ml-2">
                <button
                    class="p-2 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full text-white opacity-60 group-hover:opacity-100 transition-all duration-300 hover:scale-110 cursor-pointer"
                    @click="onPlay"
                    @touchstart.prevent="onPlay"
                    aria-label="Play playlist"
                >
                    <PlayIcon class="w-4 h-4" />
                </button>
            </Tooltip>
        </div>
    </div>
</template>

<script setup>
import { defineProps, watch, ref, onMounted, onBeforeUnmount, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { PencilIcon, CloudArrowDownIcon, ArrowPathIcon, CheckCircleIcon, ArrowPathRoundedSquareIcon } from '@heroicons/vue/24/outline'
import { StarIcon, PlayIcon } from '@heroicons/vue/24/solid'
import mediaService from '@/service/media.service'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import offlinePlaylistService from '@/service/offlinePlaylist.service'
import { appEvents } from '@/store/app.events'
import toastStore from '@/store/toast.store'

const { t } = useI18n()
const emit = defineEmits(['edit', 'favorite', 'play'])
const props = defineProps({
    playlist: {
        type: Object,
        required: true,
        default: () => ({
            name: 'Playlist Name',
            description: 'Tulus, Float, For Revenge, ...',
            songs: [],
            created_at: new Date().toISOString(),
            cover: null
        })
    },
    editVisible: {
        type: Boolean,
        default: false
    }
})
const cover = ref(props.playlist.cover)
const coverLoaded = ref(false)
const coverFailed = ref(false)
watch(cover, () => {
    coverLoaded.value = false
    coverFailed.value = false
})
const isOffline = ref(false)
const isOfflineDownloading = ref(false)
const hasUpdate = ref(false)
const offlineProgress = ref({ current: 0, total: 0, percentage: 0 })

const checkOfflineStatus = async () => {
    const id = props.playlist?._id || props.playlist?.id
    if (!id) return
    isOffline.value = await offlinePlaylistService.isPlaylistDownloaded(id)
    isOfflineDownloading.value = offlinePlaylistService.isPlaylistDownloading(id)
    if (isOffline.value && navigator.onLine) {
        const updateInfo = await offlinePlaylistService.checkPlaylistUpdates(id, props.playlist)
        hasUpdate.value = Boolean(updateInfo && updateInfo.hasUpdate)
    } else {
        hasUpdate.value = false
    }
}

const onOfflineEvent = () => {
    checkOfflineStatus()
}

const onOfflineProgress = (data) => {
    const id = props.playlist?._id || props.playlist?.id
    if (data && data.playlistId === id) {
        isOfflineDownloading.value = true
        offlineProgress.value = data
    }
}

const onOfflineStarted = (data) => {
    const id = props.playlist?._id || props.playlist?.id
    if (data && data.playlistId === id) {
        isOfflineDownloading.value = true
    }
}

const toggleOffline = async () => {
    const id = props.playlist?._id || props.playlist?.id
    if (!id) return

    if (isOfflineDownloading.value) return

    if (hasUpdate.value) {
        // One-click Sync
        try {
            isOfflineDownloading.value = true
            await offlinePlaylistService.syncPlaylist(id, (progress) => {
                offlineProgress.value = progress
            })
            hasUpdate.value = false
            isOffline.value = true
            toastStore.dispatch('addMessage', {
                message: t('dashboard.syncSuccess', { name: props.playlist.name }) || `${props.playlist.name} atualizada offline!`,
                icon: 'success'
            })
        } catch (e) {
            console.error('Error syncing playlist offline:', e)
            toastStore.dispatch('addMessage', { message: 'Erro ao sincronizar playlist offline', icon: 'error' })
        } finally {
            isOfflineDownloading.value = false
        }
    } else if (isOffline.value) {
        try {
            await offlinePlaylistService.removePlaylist(id)
            isOffline.value = false
            hasUpdate.value = false
            toastStore.dispatch('addMessage', { message: t('playlistView.removeSuccess') || 'Playlist removida do dispositivo.', icon: 'success' })
        } catch (e) {
            console.error('Error removing offline playlist:', e)
        }
    } else {
        try {
            isOfflineDownloading.value = true
            await offlinePlaylistService.downloadPlaylist(props.playlist, (progress) => {
                offlineProgress.value = progress
            })
            isOffline.value = true
            hasUpdate.value = false
            toastStore.dispatch('addMessage', { message: t('playlistView.downloadSuccess') || 'Playlist salva offline!', icon: 'success' })
        } catch (e) {
            console.error('Error downloading offline playlist:', e)
            if (e.code === 'FREE_LIMIT_REACHED' || e.message === 'FREE_LIMIT_REACHED') {
                toastStore.dispatch('addMessage', {
                    message: t('playlistView.offlineFreeLimitReached') || 'Usuários do plano gratuito podem salvar apenas 1 playlist offline.',
                    icon: 'warning'
                })
            } else {
                toastStore.dispatch('addMessage', { message: 'Erro ao baixar playlist offline', icon: 'error' })
            }
        } finally {
            isOfflineDownloading.value = false
        }
    }
}

const onEdit = () => {
    emit('edit', props.playlist)
}

const onFavorite = () => {
    emit('favorite', props.playlist)
}

const onPlay = () => {
    emit('play', props.playlist)
}

const suggestedCover = async (playlist) => {
    return await mediaService.fetchSuggestedImage(playlist)
}
const checkCover = async () => {
    if (props.playlist.cover) {
        cover.value = props.playlist.cover
        return
    }
    cover.value = await suggestedCover(props.playlist)
}
watch(() => props.playlist, async () => {
    checkCover()
    checkOfflineStatus()
})

onMounted(async () => {
    checkCover()
    checkOfflineStatus()
    appEvents.on('offline:playlist-downloaded', onOfflineEvent)
    appEvents.on('offline:playlist-removed', onOfflineEvent)
    appEvents.on('offline:playlist-download-started', onOfflineStarted)
    appEvents.on('offline:playlist-download-progress', onOfflineProgress)
    appEvents.on('offline:updates-checked', checkOfflineStatus)
    appEvents.on('offline:playlist-synced', checkOfflineStatus)
    appEvents.on('offline:all-synced', checkOfflineStatus)
})

onBeforeUnmount(() => {
    appEvents.off('offline:playlist-downloaded', onOfflineEvent)
    appEvents.off('offline:playlist-removed', onOfflineEvent)
    appEvents.off('offline:playlist-download-started', onOfflineStarted)
    appEvents.off('offline:playlist-download-progress', onOfflineProgress)
    appEvents.off('offline:updates-checked', checkOfflineStatus)
    appEvents.off('offline:playlist-synced', checkOfflineStatus)
    appEvents.off('offline:all-synced', checkOfflineStatus)
})
</script>
